import React, { useCallback, useMemo } from 'react';
import Form from 'react-bootstrap/Form';
import DropdownSelect from '../Generic/DropdownSelect';
import { findNode, readSlotCount } from '../../utils/storageTree';
import {
  buildStoragePlaceOptions,
  filterStoragePlaceOptions,
  formatStoragePlace,
} from '../../utils/storagePlace';
import { logInfo } from '../../config_and_helpers/logger';

/**
 * Searchable picker for any storage node. Search matches the place name or any
 * ancestor (a room query lists that room and everything under it). A slotted
 * box then asks for a slot. `storageNodeId` empty means the lot is not placed.
 *
 * Pass a shared `options` list from the parent when many pickers share one tree
 * so each row does not rebuild path labels.
 *
 * @param {Object} props
 * @param {Array} props.nodes
 * @param {Array<{ value: string, label: string }>|null} [props.options] - Prebuilt options from `buildStoragePlaceOptions`
 * @param {string|number|null} props.storageNodeId
 * @param {string|number|null} props.slotIndex
 * @param {Function} props.onChange - ({ storageNodeId, slotIndex }) => void
 * @param {boolean} [props.readOnly]
 * @param {string} [props.idPrefix]
 */
const StoragePlaceSelect = ({
  nodes,
  options: sharedOptions = null,
  storageNodeId,
  slotIndex,
  onChange,
  readOnly = false,
  idPrefix = 'storage-place',
}) => {
  const place = findNode(nodes, storageNodeId);
  const slotCount = readSlotCount(place);
  const label = formatStoragePlace(nodes, storageNodeId, slotIndex) || '—';
  const localOptions = useMemo(
    () => (sharedOptions ? null : buildStoragePlaceOptions(nodes)),
    [nodes, sharedOptions]
  );
  const options = sharedOptions || localOptions || [];
  const matched = options.find(
    option => option.value !== '' && String(option.value) === String(storageNodeId ?? '')
  );
  // An unplaced lot stays an empty field. Putting "Not placed" in the input
  // blocks typing a search, because the control keeps restoring that label.
  const selected = matched || null;
  const filterOptions = useCallback(
    (optionList, params) =>
      filterStoragePlaceOptions(optionList, params?.inputValue ?? '', nodes),
    [nodes]
  );

  if (readOnly) {
    return <span>{label}</span>;
  }

  return (
    <div className="storage-place-select">
      <DropdownSelect
        optionsList={options}
        label="Storage place"
        placeholder="Search a place"
        clearable
        filterOptions={filterOptions}
        selectedValue={selected}
        setSelectedValue={next => {
          const nextId = next?.value;
          logInfo('StoragePlaceSelect', 'storage place chosen', {
            storageNodeId: nextId || null,
          });
          onChange?.({
            storageNodeId: nextId == null || nextId === '' ? null : nextId,
            slotIndex: null,
          });
        }}
      />
      {slotCount ? (
        <>
          <Form.Label htmlFor={`${idPrefix}-slot`} className="visually-hidden">
            Slot
          </Form.Label>
          <Form.Select
            id={`${idPrefix}-slot`}
            className="mt-1"
            size="sm"
            aria-label="Slot"
            value={slotIndex == null || slotIndex === '' ? '' : String(slotIndex)}
            onChange={event => {
              const nextSlot = event.target.value;
              onChange?.({
                storageNodeId,
                slotIndex: nextSlot === '' ? null : Number(nextSlot),
              });
            }}
          >
            <option value="">Choose a slot</option>
            {Array.from({ length: slotCount }, (_, index) => index + 1).map(slot => (
              <option key={slot} value={slot}>
                {slot}
              </option>
            ))}
          </Form.Select>
        </>
      ) : null}
    </div>
  );
};

export default StoragePlaceSelect;
